<!-- eslint-disable no-unused-vars -->
<template>
  <div>
    <template v-if="session && uiStore.settingsLoaded">
      <Suspense>
        <div>
          <AppointmentsModify
            v-model:open="appointmentsStore.modifyDialogOpen"
            :appointment="appointmentsStore.selectedAppointment"
            :current-company="appointmentsStore.currentCompany"
            @changed="onAppointmentEntryChanged"
            @edit="onAppointmentEntryEdit"
          />
          <AppointmentsEntry
            v-if="appointmentsStore.storeData"
            v-model:open="appointmentsStore.entryDialogOpen"
            :current-company="appointmentsStore.currentCompany"
            :companies="appointmentsStore.companies"
            :services-list="appointmentsStore.services"
            :resources-list="appointmentsStore.resources"
            @updated="onAppointmentEntryUpdated"
          />
          <Header v-show="uiStore.showAppHeader" />
          <router-view v-slot="{ Component }">
            <transition name="fade" mode="out-in">
              <KeepAlive include="HomeView,StockTransfersView" exclude="ProductView">
                <component :is="Component" />
              </KeepAlive>
            </transition>
          </router-view>

          <CustomerInvalidEmailWarning v-if="customerStore.hasInvalidEmail" />
        </div>
      </Suspense>
      <div
        v-if="appOutdated"
        style="position: fixed; width: 420px; top: 8px; right: 24px; z-index: 999"
      >
        <sl-alert variant="warning" open>
          <sl-icon slot="icon" name="arrow-clockwise"></sl-icon>
          <strong>A new version of Wings is available</strong><br />
          Please complete the current action and than click update to avoid any errors.<br /><br />
          <sl-button variant="primary" size="medium" @click="reloadApp()">Update app now</sl-button>
        </sl-alert>
      </div>
    </template>
    <template v-else>
      <div
        style="
          display: flex;
          gap: 30px;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100vh;
        "
      >
        <div
          v-for="(error, index) in initErrors"
          :key="index"
          style="width: 524px; margin-top: 20px"
        >
          <sl-alert variant="danger" open>
            <sl-icon slot="icon" name="exclamation-octagon"></sl-icon>
            <strong>{{ $t(error.message) }}</strong>
          </sl-alert>
        </div>
        <Auth />
      </div>
    </template>

    <Broadcast />

    <div
      v-if="!online"
      style="position: fixed; width: 420px; top: 8px; right: 24px; z-index: 10000"
    >
      <sl-alert variant="danger" open>
        <sl-icon slot="icon" name="exclamation-octagon"></sl-icon>
        <strong>{{ $t('app.network_status_offline') }}</strong
        ><br />
        {{ $t('app.network_status_offline_message') }}
      </sl-alert>
    </div>

    <div
      v-if="
        uiStore.settingsLoaded &&
        (deviceStore.settings?.warehouse?.length <= 2 ||
          !deviceStore.settings?.currentEmployee?.eid ||
          deviceStore.settings?.currentEmployee?.eid === '')
      "
      style="position: fixed; width: 420px; top: 8px; right: 24px; z-index: 10000"
    >
      <sl-alert variant="danger" open>
        <sl-icon slot="icon" name="exclamation-octagon"></sl-icon>
        <strong>{{ $t('app.corruptSettings') }}</strong
        ><br />
        {{ $t('app.corruptSettingsText') }}
      </sl-alert>
    </div>

    <div v-if="session">
      <DeviceMarketSettingsDialog />
    </div>
  </div>
</template>

<script setup>
import AppointmentsModify from '@/components/Appointments/AppointmentsModify/AppointmentsModify.vue'
import AppointmentsEntry from '@/components/Appointments/AppointmentsEntry/AppointmentsEntry.vue'
import Auth from '@/components/Auth.vue'
import Broadcast from '@/components/Broadcast.vue'
import DeviceMarketSettingsDialog from '@/components/Device/DeviceMarketSettingsDialog.vue'
import Header from '@/components/Layout/Header/Header.vue'
import { useCustomer } from '@/composables/useCustomer.js'
import { initTypesenseKey } from '@/composables/useTypesense'
import { initGoogleMapsKey } from '@/composables/useGoogleMaps'
import { initSecrets } from '@/composables/useSecrets'
import { useEmployee } from '@/composables/useEmployee.js'
import { useMarket } from '@/composables/useMarket.js'
import { useNotification } from '@/composables/useNotification.js'
import { useShipment } from '@/composables/useShipment'
import { MODE } from '@/constants/device.js'
import { useAppointmentsStore } from '@/stores/appointments'
import { useCustomerStore } from '@/stores/customer.js'
import { useDeviceStore } from '@/stores/device'
import { useUiStore } from '@/stores/ui'
import '@shoelace-style/shoelace/dist/components/alert/alert'
import '@shoelace-style/shoelace/dist/components/button/button'
import '@shoelace-style/shoelace/dist/components/icon/icon'
import { useOnline } from '@vueuse/core'
import { defineAsyncComponent, inject, onBeforeMount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterView } from 'vue-router'

const supabase = inject('supabase')
const initErrors = inject('initErrors')
const session = ref(true) // to avoid flickering of the auth page.
const deviceStore = useDeviceStore()
const appointmentsStore = useAppointmentsStore()
const uiStore = useUiStore()
const online = useOnline()
const { t } = useI18n()
const notification = useNotification()
const { employee, addEmployee, buildEmployee, setCurrentEmployee, getEmployeeList } = useEmployee()
const { fetchAvailableAllShippingMethods } = useShipment()
const { getCityVouchers } = useMarket()
const { loadCustomerByEmail } = useCustomer()
const customerStore = useCustomerStore()
const appOutdated = ref(false)

const CustomerInvalidEmailWarning = defineAsyncComponent(
  () => import('@/components/Customer/CustomerInvalidEmailWarning.vue')
)

async function setPreLoginSettingsData() {
  const marketSettings = localStorage.getItem('marketSettings')
  if (marketSettings?.length) {
    const settings = JSON.parse(marketSettings)
    let mode = MODE.STORE
    if (['SHAT', 'SHCH', 'SHDE'].includes(settings.warehouse)) {
      mode = MODE.SERVICE_HUB
    }

    // if availableShippingMethods not set on reload fetch it
    let availableShippingMethods = settings.availableShippingMethods
    if (!availableShippingMethods || availableShippingMethods?.length === 0) {
      availableShippingMethods = await fetchAvailableAllShippingMethods()
    }

    deviceStore.$patch({
      settings: {
        stockLocationId: settings.stockLocationId,
        warehouse: settings.warehouseReference ? settings.warehouseReference : settings.warehouse, // Legacy for old local settings
        deviceType: settings.deviceType,
        deviceId: settings.deviceId === '' ? deviceStore.user.email : settings.deviceId, // Legacy. currently this might be empty
        country: settings.country ? settings.country : settings.countryCode, // Legacy for old local settings
        currency: settings.currency ? settings.currency : settings.currencyCode, // Legacy for old local settings
        language: settings.language,
        name: settings.warehouse,
        marketId: settings.marketId,
        storeAddressId: settings.storeAddressId,
        serviceHubId: settings.serviceHubId,
        assemblyOnlyInServiceHub: settings.assemblyOnlyInServiceHub,
        storeType: settings.storeType ?? null,
        keepOpenInWings: settings.keepOpenInWings ?? false,
        tags: settings.tags,
        mode: mode,
        availableShippingMethods: availableShippingMethods,
        cityVouchers: await getCityVouchers(settings.marketId)
      }
    })
  }
  uiStore.settingsLoaded = true
}

async function setUserSettings(user) {
  deviceStore.setStoreUser(user)
  await addEmployee(user.email)

  // throw error if no data - we strongly expect data
  if (!employee.value) {
    session.value = false
    await supabase.auth.signOut()
  }
}

let employeeChannel = null

function attachWebSocket() {
  // re-attaching (e.g. after a token refresh) must replace the channel, not stack a new one
  if (employeeChannel) {
    supabase.removeChannel(employeeChannel)
  }

  employeeChannel = supabase
    .channel('schema-db-changes')
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'employees'
        // no id=in.(...) filter: large employee lists exceed the realtime.subscription
        // index row size limit and the failed subscribe retries forever — filter client-side
      },
      (payload) => {
        const sessionUserEmail = deviceStore.user?.email
        if (sessionUserEmail === payload.new.email && !payload.new.auth_user_id) {
          // if the auth ID is not set means the user was deleted
          supabase.auth.signOut()
          session.value = false
        } else if (deviceStore.settings?.currentEmployee?.id === payload.new.id) {
          // if is current employee also update the current employee
          setCurrentEmployee(payload.new)
        } else {
          // find employee in the list and update it
          const employeeIndex = deviceStore.settings?.employees?.findIndex(
            (employee) => employee.id === payload.new.id
          )

          if (employeeIndex !== undefined && employeeIndex !== -1) {
            const updatedEmployee = buildEmployee(payload.new)
            deviceStore.addStoreEmployee(updatedEmployee)
          }
        }
      }
    )
    .subscribe()
}

async function setSessionData(userSession) {
  if (!userSession) {
    supabase.removeAllChannels()
    employeeChannel = null
    deviceStore.$reset
    session.value = false
    return
  }

  const { user } = userSession

  // if no user reset session
  if (!user) {
    session.value = false
    notification.showNotification({
      message: t('app.no_active_user'),
      variant: 'danger'
    })
  } else {
    await setUserSettings(user)
    if (employee.value) {
      await setPreLoginSettingsData()
      await getEmployeeList(deviceStore.settings.warehouse)
      attachWebSocket()
    }
  }
}

onBeforeMount(() => {
  const mqStandAlone = '(display-mode: standalone)'
  if (navigator.standalone || window.matchMedia(mqStandAlone).matches) {
    uiStore.isHomescreenApp = true
  }
})

onMounted(async () => {
  // get the session
  supabase.auth.getSession().then(async ({ data }) => {
    if (data?.session?.access_token && data?.session?.refresh_token) {
      await supabase.auth.setSession({
        access_token: data.session.access_token,
        refresh_token: data.session.refresh_token
      })
      await initSecrets()
      initTypesenseKey()
      initGoogleMapsKey()
    }

    session.value = data.session
  })

  // https://supabase.com/docs/reference/javascript/auth-onauthstatechange
  supabase.auth.onAuthStateChange(async (event, _session) => {
    if (event === 'SIGNED_OUT') {
      session.value = false
    } else if (event === 'TOKEN_REFRESHED') {
      session.value = _session
    } else if (event === 'USER_UPDATED') {
      session.value = _session
    }
  })

  if (process.env.NODE_ENV === 'production' && session) {
    window.addEventListener('pageshow', getVersionEvery4min())
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        getVersionEvery4min()
      }
    })
  }
})

// watch session for changes
watch(session, async (newSession) => {
  if (newSession) {
    await setSessionData(newSession)
  }
})

async function getVersionFromEndpoint() {
  try {
    const response = await fetch('/api/version')
    if (!response.ok) {
      throw new Error('Failed to fetch data')
    }
    const data = await response.json()
    return data.version
  } catch (error) {
    console.error('Error fetching data:', error)
    return null
  }
}

async function getVersionEvery4min() {
  try {
    const version = await getVersionFromEndpoint()
    if (version !== null) {
      // eslint-disable-next-line no-undef
      if (version !== __APP_VERSION__) {
        appOutdated.value = true
      }
    }
  } catch (error) {
    console.error('Error:', error)
  }
}

function reloadApp() {
  window.location.reload(true)
}

if (process.env.NODE_ENV === 'production' && session) {
  getVersionEvery4min()

  setInterval(() => {
    getVersionEvery4min()
  }, 240000)
}

// appointments
async function onAppointmentEntryChanged() {
  appointmentsStore.modifyDialogOpen = false
  await appointmentsStore.updateAppointments()
}

async function onAppointmentEntryEdit() {
  const email = appointmentsStore.selectedAppointment.singleCustomers?.[0]?.email
  if (email) {
    const customer = await loadCustomerByEmail(email)
    if (customer) {
      customerStore.setCustomer(customer)
    }
    appointmentsStore.entryDialogOpen = true
    // internal appointment have no customer
  } else if (!appointmentsStore.selectedAppointment.isBookable) {
    appointmentsStore.entryDialogOpen = true
  }
}

function onAppointmentEntryClose() {
  appointmentsStore.entryDialogOpen = false
}

async function onAppointmentEntryUpdated() {
  onAppointmentEntryClose()
  await appointmentsStore.updateAppointments()
}
</script>

<style>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
